import { useEffect, useState } from 'react';

const HIGH_CONTRAST_STORAGE_KEY = 'chromis-high-contrast-mode';

/**
 * Custom hook for managing high-contrast accessibility mode with localStorage persistence.
 * Applies WCAG AAA compliant contrast ratios (7:1 minimum) for improved accessibility.
 * 
 * @returns Object containing high-contrast state and setter function
 */
export function useHighContrast() {
  // Get initial state from localStorage or default to false
  const [isHighContrast, setHighContrastState] = useState<boolean>(() => {
    const stored = localStorage.getItem(HIGH_CONTRAST_STORAGE_KEY);
    return stored === 'true';
  });

  // Update high-contrast mode and persist to localStorage
  const setHighContrast = (enabled: boolean) => {
    setHighContrastState(enabled);
    localStorage.setItem(HIGH_CONTRAST_STORAGE_KEY, String(enabled));
  };

  // Apply high-contrast mode to document
  useEffect(() => {
    if (isHighContrast) {
      document.documentElement.setAttribute('data-high-contrast', 'true');
    } else {
      document.documentElement.removeAttribute('data-high-contrast');
    }
  }, [isHighContrast]);

  return {
    isHighContrast,
    setHighContrast,
  };
}
